import { Box, Divider, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { FunctionComponent, useState } from 'react';
import { EXPERIMENTS } from '../../../../constants';
import { DATA_HOOKS } from '../../../../dataHooks';
import { ArticleSearchResultItem } from '../../../../types';
import { InnerLink } from '../../../InnerLink';
import css from '../index.module.scss';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { SearchResultItem } from '../utils';
import { SearchResultDropdownItem } from './SearchResultDropDownItem';
import { DeprecatedSearchBarResults } from '../DeprecatedSearchBarResults';

export type ResultsResourceGroupProps = {
  searchResults: ArticleSearchResultItem[] | SearchResultItem[];
  inputText: string;
  focusedArticleIndex: number;
  onSearchResultClick: (
    article: ArticleSearchResultItem,
    index: number
  ) => Promise<void>;
  setFocusedArticleIndex: (index: number) => void;
  viewLimit?: number;
};

export const ResultsResourceGroup: FunctionComponent<
  ResultsResourceGroupProps
> = ({
  searchResults,
  inputText,
  focusedArticleIndex,
  onSearchResultClick,
  setFocusedArticleIndex,
  viewLimit,
}) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments({ suspense: true, readOnly: true });
  const [focusedTypedArticleIndex, setFocusedTypedArticleIndex] =
    useState<number>(-1);

  const isExternalSearchResultsEnabled = experiments.enabled(
    EXPERIMENTS.SPECS.EXTERNAL_SEARCH
  );

  const getArticleType = (
    article: ArticleSearchResultItem | SearchResultItem
  ) => {
    if (!article?.type) {
      const searchResultItem = article as SearchResultItem;
      return searchResultItem.type;
    } else {
      return (article as ArticleSearchResultItem).type;
    }
  };

  const showMoreArticles =
    viewLimit && searchResults.length - viewLimit > 0
      ? searchResults.length - viewLimit
      : 0;

  const isResultUnfolded = (resultsIndex: number) =>
    viewLimit && resultsIndex < viewLimit;

  return (
    <>
      {isExternalSearchResultsEnabled ? (
        <Box className={css.searchBarResultsList} direction="vertical">
          <Box
            direction="vertical"
            dataHook={`${DATA_HOOKS.SEARCH_BAR_RESULTS}_${searchResults[0].type}`}
          >
            <Box
              align="space-between"
              className={css.resultsGroupTitle}
              verticalAlign="middle"
              paddingRight="SP4"
              paddingLeft="SP4"
              height="36px"
            >
              <Text
                size="small"
                weight="normal"
                color={'D30'}
                className={css.searchBarResultTypeTitle}
                dataHook={DATA_HOOKS.SEARCH_BAR_RESULT_TYPE_TITLE}
              >
                {t(
                  `search-bar-results.type.group.${getArticleType(
                    searchResults[0]
                  )}`
                )}
              </Text>
              {showMoreArticles ? (
                <InnerLink
                  href={`/search?term=${inputText}`}
                  className={css.seeMoreResults}
                  dataHook={`${DATA_HOOKS.SEARCH_BAR_RESULT_TYPE_MORE}_${searchResults[0].type}`}
                  onMouseEnter={() => setFocusedTypedArticleIndex(-1)}
                >
                  <Text
                    size="small"
                    weight="thin"
                    className={css.seeMoreResultsText}
                  >
                    {t('searchbar.show-more', { count: showMoreArticles })}
                  </Text>
                </InnerLink>
              ) : null}
            </Box>
            <Box
              className={css.searchBarResultsList}
              align="left"
              direction="vertical"
            >
              <Divider />

              {searchResults.map((searchResult, index) =>
                isResultUnfolded(index) ? (
                  <SearchResultDropdownItem
                    searchResult={searchResult}
                    index={index}
                    key={index}
                    onSearchResultClick={onSearchResultClick}
                    isFocused={focusedTypedArticleIndex === index}
                    onMouseEnter={() => setFocusedTypedArticleIndex(index)}
                    onMouseLeave={() => setFocusedTypedArticleIndex(-1)}
                  />
                ) : null
              )}
            </Box>
            <Divider />
            <div aria-live="assertive" key={inputText} className={css.srOnly}>
              <>
                {t(`searchbar.results.reader`, { count: searchResults.length })}
              </>
            </div>
          </Box>
        </Box>
      ) : (
        <DeprecatedSearchBarResults
          articles={searchResults as ArticleSearchResultItem[]}
          inputText={inputText}
          focusedArticleIndex={focusedArticleIndex}
          onSearchResultClick={onSearchResultClick}
          setFocusedArticleIndex={setFocusedArticleIndex}
        />
      )}
    </>
  );
};
